import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import SVG, { stopDrag, stopResize, unselectize } from '../../../../../utils/svg';

import store from "../../../../../store/index";
import {
    setStoreData,
    setIsSceneUpdated,
    setIsInfoWindowVisible,
    setSelectedStoreElement,
    setUnsavedModifications,
    setIsWallDrawEnabled,
    setZoomToElementId,
    updateFloorPlanElement,
    addFloorPlanElement,
    updateRuler,
    removeFloorPlanElement,
    removeRuler
} from "../../../../../store/actions/Store";

import apiClient from '../../../../../services/api';

import {
    generateUniqueString,
    getRotatedElementWrapperSize
} from '../../../../../utils/Utils';

import { IS_DEV } from '../../../../../isdev';

import './Elements.css';

export const ELEMENTS_LAYER_ID = 'ElementsLayer';

const defaultFillColor = 'black';
const defaultStrokeColor = 'rgba(0,0,0,0.3)';

const wallFillColor = 'rgba(31,31,31,0.8)';
const wallStrokeColor = 'rgba(0,0,0,0.5)';

const rulerWidth = 3;
const rulerColor = '#00ff00';

const snapDistance = 50;

const frameFillColor = 'yellow';
const frameStrokeColor = 'gray';

// compare elements (projects, walls, rulers)
// TODO: move to store utils
const compareElements = (el1, el2, type) => {
    if((el2.type == 'vertical' || el2.type == 'horizontal') && type != 'ruler') { return };

    if (!el1 || !el2) return false;
    return (el1.generated_id && el1.generated_id === el2.generated_id)
        || (el1.id && el1.id === el2.id);
};

// returns actual state of store
// TODO: move to store utils
export const getStoreState = () => {
    const currentState = store.getState();
    return currentState.store;
};

// returns floorplan size vars
// TODO: move to store utils
export const getStoreSizes = () => {
    const { storeData } = getStoreState();
    const st = storeData?.floor_plan;
    const stw = st?.width;
    const std = st?.depth;
    const gw = st?.grid_width;
    const gd = st?.grid_depth;
    return { stw, std, gw, gd };
};

// returns current element state
// TODO: move to store utils
export const getStoreElement = (element) => {
    const { storeData } = getStoreState();
    const storeElements = storeData?.floor_plan_elements;
    return storeElements ? storeElements.find(el => compareElements(element, el)) : null;
};

/**
 * Get transformed element position after drag
 * @param {Node} node
 * @param {object} element 
 * @param {array} size 
 * @param {string} type 
 * @returns 
 */
const getTransformedElementPosition = (node, element, end = false) => {
    const { positionX: px, positionY: py } = element;
    const result = { x: Number(px), y: Number(py) };

    const transformation = node.computedStyleMap().get('transform');
    if (!transformation || !transformation[0]) return result;
    
    const matrix = transformation[0].matrix;

    result.x += matrix['e'];
    result.y += matrix['f'];

    if (end) {
        const { storeData } = getStoreState();

        const st = storeData?.floor_plan;
        const stw = st?.width;
        const std = st?.depth;

        const size = getRotatedElementWrapperSize(element);

        if (result.x < 0) {
            result.x = 0;
        }
        if (result.y < 0) {
            result.y = 0;
        }
        if (result.x > stw - size.width) {
            result.x = stw - size.width;
        }
        if (result.y > std - size.depth) {
            result.y = std - size.depth;
        }
    }

    return result;
};

// creates eleent based drag function
const makeSnapToGrid = (element) => {
    const el = getStoreElement(element);
    if (!el) return;

    const { stw, std, gw, gd } = getStoreSizes();
    const { snapToRuler, storeData, isGridEnabled } = getStoreState();

    const isRotated = Number(el.angle) !== 0;

    const { width: ew, depth: ed } = isRotated
        ? getRotatedElementWrapperSize(el) : el;
    
    return (x, y) => {
        const pos = {
            x: x < 0 ? 0 : x,
            y: y < 0 ? 0 : y,
        };

        pos.x = x > stw - ew ? stw - ew : Math.round(pos.x);
        pos.y = y > std - ed ? std - ed : Math.round(pos.y);

        // snap to rulers
        if (snapToRuler) {
            const sd = snapDistance;
            const rulers = storeData?.rulers;
            if (rulers) rulers.forEach(ruler => {
                const v = ruler.type === 'vertical';
                const rx = Number(ruler.x);
                const ry = Number(ruler.y);
                if (v && (pos.x < rx + sd && pos.x > rx - sd)) {
                    pos.x = rx;
                }
                if (!v && (pos.y < ry + sd && pos.y > ry - sd)) {
                    pos.y = ry;
                }
            });
        }

        // snap to grid
        if (isGridEnabled && gw > 1 && gd > 1) {
            pos.x = pos.x - pos.x % gw;
            pos.y = pos.y - pos.y % gd;
        }

        return pos;
    }
};

// snap coordinates on drag
const draggableSnapToGrid = function(x, y) {
    const {
        isDragEnabled,
        isGridEnabled,
    } = getStoreState();

    if (!isDragEnabled) return false;

    const { stw, std, gw, gd } = getStoreSizes();

    if (isGridEnabled && gw > 0 && gd > 0) {
        return { x: x - x % gw, y: y - y % gd };
    }

    return { x, y };
};

/**
 * Removes the selected floor plan element from REDUX
 * @returns 
 */
const removeElement = () => {
    const { selectedStoreElement } = getStoreState();
    if (!selectedStoreElement) return;

    if (window.confirm('Are you sure you want to delete selected element?')) {
        if(selectedStoreElement.type == 'vertical' || selectedStoreElement.type == 'horizontal') {
            store.dispatch(removeRuler(selectedStoreElement));
        }

        store.dispatch(removeFloorPlanElement(selectedStoreElement));
        selectStoreElement(null, false);
    }
};

/**
* Handle delete key press listener
* @param {KeyboardEvent} event 
*/
const handleKeyPressDelete = (event) => {
    if (event.key === 'Delete') {
        removeElement();
    }
};

// simple snap to grid calc
const calcSnap = (gw, gd) => Math.max(Math.min(gw, gd), 1);

// returns new wall element from svg rect node
const wallFromRect = (node, stid) => ({
    'id': 0,
    'is_wall': 1,
    'generated_id': generateUniqueString(),
    'store_id': stid,
    'width': node.getAttribute('width'),
    'depth': node.getAttribute('height'),
    'positionX': node.getAttribute('x'),
    'positionY': node.getAttribute('y'),
    'positionZ': 0,
    'angle' : 0,
});

// TODO: move somewhere
// select element and show window
export const selectStoreElement = (element, show) => {
    store.dispatch(setSelectedStoreElement(element));
    if (show !== undefined) {
        store.dispatch(setIsInfoWindowVisible(show));
    }
};

const ElementsLayer = ({ scale, floorPlanId, setContextMenu }) => {
    const [hiddenElements, setHiddenElements] = useState({});

    // const [isWallPolygonDrawEnabled, setIsWallPolygonDrawEnabled] = useState(false);

    const storeData = useSelector((state) => state.store.storeData);

    const selected = useSelector((state) => state.store.selectedStoreElement);
    const isSceneUpdated = useSelector((state) => state.store.isSceneUpdated);

    const moveElementsWithRuler = useSelector((state) => state.store.moveElementsWithRuler);
    const snapToRuler = useSelector((state) => state.store.snapToRuler);

    const isDragEnabled = useSelector((state) => state.store.isDragEnabled);
    const isGridEnabled = useSelector((state) => state.store.isGridEnabled);
    const isWallDrawEnabled = useSelector((state) => state.store.isWallDrawEnabled);

    const zones = useSelector((state) => state.store.zones);
    const zonesVisibility = useSelector((state) => state.store.zonesVisibility);

    const currentTab = useSelector((state) => state.store.currentTab);

    const st = storeData?.floor_plan; // store

    const storeElements = useMemo(() => storeData?.floor_plan_elements || [], [storeData?.floor_plan_elements]);
    const elements = useMemo(() => storeElements.filter(item => !item.is_wall), [storeElements]);
    const walls = useMemo(() => storeElements.filter(item => item.is_wall), [storeElements]);

    const rulers = useMemo(() => storeData?.rulers || [], [storeData?.rulers]);

    const stid = st ? st.id : null; // store id

    const fpid = st ? st.floor_plan_id : null; // floor plan id

    const stw = st ? st.width : 100; // store width
    const std = st ? st.depth : 100; // store depth

    const gw = st ? st.grid_width : 100; // grid width
    const gd = st ? st.grid_depth : 100; // grid height

    const snap = calcSnap(gw, gd);

    const up = currentTab !== 'zones';

    /**
     * Initialize wall draw listener
     */
    const drawWall = () => {
        const rect = new SVG(ELEMENTS_LAYER_ID).rect()
            .fill(wallFillColor).stroke(wallStrokeColor);

        rect.draw({ snapToGrid: isGridEnabled ? snap : 1 });

        rect.on('drawstop', function() {
            const { node } = this;

            const wall = wallFromRect(node, stid);

            rect.remove();

            selectStoreElement(wall, true);

            store.dispatch(addFloorPlanElement(wall));
            store.dispatch(setUnsavedModifications(true));
            store.dispatch(setIsWallDrawEnabled(false));
            //store.dispatch(setHistory(btoa(JSON.stringify(storeData.floor_plan_elements))));
            //store.dispatch(setHistoryStep(historyStep+1));
        });
    };
    
    /* // currently unused
    const drawWallPolygon = () => {
        const polygon = new SVG(ELEMENTS_LAYER_ID).polygon()
            .fill('none').style({
                stroke: 'rgb(0, 0,0)',
                'stroke-width': '20',
                cursor: 'move',
            });

        polygon.draw({ snapToGrid: isGridEnabled ? snap : 1 });

        polygon.on('drawstart', function(e) {
            document.addEventListener('keydown', function(e) {
                if (e.key == 'Enter') {
                    polygon.draw('done');
                    polygon.off('drawstart');
                }
            });
        });

        polygon.on('drawstop', function() {
            setIsWallPolygonDrawEnabled(false);
        });
    }; // */

    // show element context menu
    const handleElementContextMenu = (event, element) => {
        event.preventDefault();
        setContextMenu({ mouseX: event.clientX + 2, mouseY: event.clientY - 6 });
        selectStoreElement(element);
    };

    // delete selected element on press delete
    useEffect(() => {
        window.addEventListener('keydown', handleKeyPressDelete);
        return () => window.removeEventListener('keydown', handleKeyPressDelete);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // update storeData on floorPlanId change
    useEffect(() => {
        if (fpid === Number(floorPlanId)) return;
        apiClient.post('/api/easy-floor/get', { id: Number(floorPlanId) }).then(response => {
            const floorPlan = { ...response.data };

            floorPlan.floor_plan.width = response.data.floor_plan.width * response.data.floor_plan.scale;
            floorPlan.floor_plan.depth = response.data.floor_plan.depth * response.data.floor_plan.scale;
            floorPlan.texture.width = response.data.texture.width * response.data.floor_plan.scale / 10;
            floorPlan.texture.depth = response.data.texture.height * response.data.floor_plan.scale / 10;

            store.dispatch(setStoreData(floorPlan));
            store.dispatch(setIsInfoWindowVisible(false));
            store.dispatch(setZoomToElementId(ELEMENTS_LAYER_ID));
            // store.dispatch(setHistory(btoa(JSON.stringify(storeData.floor_plan_elements))));
            // store.dispatch(setHistoryStep(1));
        });
    }, [floorPlanId, fpid]);
    
    // currently unused
    useEffect(() => {
        if (isSceneUpdated) {
            if (IS_DEV) console.log('isSceneUpdated');
            store.dispatch(setIsSceneUpdated(false));
        }
    }, [isSceneUpdated]); // eslint-disable-line react-hooks/exhaustive-deps

    // update hidden elements on zones or elements change
    useEffect(() => {
        if (!zones || !storeElements || !storeElements.length) return;

        const hidden = {};
        const hiddenZones = zones.filter(zone => !zonesVisibility[zone.id]);
        storeElements.forEach((element) => {
            const id = element.generated_id || element.id;
            hidden[id] = false;
            hiddenZones.forEach(zone => {
                const zpx = zone ? Number(zone.position_x) : 0;
                const zpy = zone ? Number(zone.position_y) : 0;
                const zw = zone ? Number(zone.width) : 0;
                const zd = zone ? Number(zone.depth) : 0;
                const px = Number(element.positionX);
                const py = Number(element.positionY);
                const inside = px >= zpx && px <= zpx + zw && py >= zpy && py <= zpy + zd;
                if (inside) hidden[id] = true;
            });
        });
        setHiddenElements(hidden);
    }, [zonesVisibility, zones, storeElements]);

    // draw walls
    useEffect(() => {
        if (isWallDrawEnabled) {
            drawWall();
        }
        /*if (isWallPolygonDrawEnabled) {
            drawWallPolygon();
        }*/
    }, [isWallDrawEnabled/*,isWallPolygonDrawEnabled*/]); // eslint-disable-line react-hooks/exhaustive-deps

    // unselect current element on grid or snap change
    useEffect(() => {
        selectStoreElement(null, false);
    }, [snapToRuler, isGridEnabled, gw, gd]);

    return (
        <svg
            id={ELEMENTS_LAYER_ID}
            width={stw}
            height={std}
            style={{ position: 'relative', opacity: up ? 1 : 0.5, overflow: 'visible' }}
        >
            <Floor
                width={stw}
                depth={std}
                scale={scale}
            />
            <g id="WallsGroup">
                {walls.map((wall) => {
                    const id = wall.generated_id || wall.id;
                    return !hiddenElements[id] && (
                        <Wall
                            key={id}
                            wall={wall}
                        />
                    );
                })}
            </g>
            <g id="ElementsGroup">
                {elements.map((element) => {
                    const id = element.generated_id || element.id;
                    return !hiddenElements[id] &&  (
                        <Element
                            key={id}
                            scale={scale}
                            element={element}
                            onContextMenu={handleElementContextMenu}
                        />
                    );
                })}
            </g>
            <g id="RulersGroup" opacity={0.5}>
                {rulers.map((ruler) => {
                    const id = ruler.generated_id || ruler.id;
                    const { isGridEnabled } = getStoreState();

                    if(isGridEnabled) {
                        return null;
                    }

                    return (
                        <Ruler
                            key={id}
                            item={ruler}
                            maxWidth={stw}
                            maxHeight={std}
                            scale={scale}
                        />
                    );
                })}
            </g>
        </svg>
    );
};



export const FLOOR_ELEMENT_ID = 'FloorElement';

// Floor rect
const Floor = ({ width, depth, scale, onClick }) => {
    const isWallDrawEnabled = useSelector((state) => state.store.isWallDrawEnabled);

    const handleClick = (event) => {
        if (isWallDrawEnabled) return;
        if (onClick) onClick();
        selectStoreElement(FLOOR_ELEMENT_ID, true);
    };

    return (
        <rect
            id={FLOOR_ELEMENT_ID}
            x={0}
            y={0}
            width={width}
            height={depth}
            fill={'dimgray'}
            fillOpacity={0.05}
            stroke={'dimgray'}
            strokeOpacity={0.2}
            strokeWidth={1/scale}
            onClick={handleClick}
        />
    );
};

const resizePointsSize = 12;

const getRectData = (node) => {
    const data = {
        x: node.getAttribute('x'),
        y: node.getAttribute('y'),
        width: node.getAttribute('width'),
        height: node.getAttribute('height'),
    };
    return data;
};

const handleWallMouseUp = (event, wall) => {
    const { target } = event;

    const data = getRectData(target);

    if (
        wall.positionX !== data.x || wall.positionY !== data.y
        || wall.width !== data.width || wall.depth !== data.height
    ) {
        const currentWall = getStoreElement(wall);

        const updatedWall = {
            ...currentWall,
            positionX: Math.round(Number(data.x)),
            positionY: Math.round(Number(data.y)),
            width: Math.floor(Number(data.width)),
            depth: Math.floor(Number(data.height)),
        };

        store.dispatch(updateFloorPlanElement(updatedWall));

        selectStoreElement(updatedWall);
    }

    showInfoWindow();
};

// const onWallDragMove = (event, wall) => {};

const onWallDragEnd = (event, wall) => {
    const { target } = event;

    const data = getRectData(target);

    if (wall.positionX !== data.x || wall.positionY !== data.y) {
        const currentWall = getStoreElement(wall);

        const updatedWall = {
            ...currentWall,
            positionX: Math.round(Number(data.x)),
            positionY: Math.round(Number(data.y)),
        };

        store.dispatch(updateFloorPlanElement(updatedWall));

        selectStoreElement(updatedWall);
    }

    showInfoWindow();
};

const enableWallDrag = (node, wall) => {
    if (!node) return;

    const rect = SVG.adopt(node);

    rect.draggable(makeSnapToGrid(wall));
    rect.on('beforedrag', hideInfoWindow);
    // rect.on('dragmove.namespace', (e) => onWallDragMove(e, wall));
    rect.on('dragend', (e) => onWallDragEnd(e, wall));
};

const enableWallResize = (node, wall) => {
    if (!node) return;

    const { isGridEnabled } = getStoreState();
    const { stw, std, gw, gd } = getStoreSizes();

    const snap = calcSnap(gw, gd);

    const rect = SVG.adopt(node);

    rect.selectize({
        rotationPoint: false,
        pointSize: resizePointsSize,
    });

    rect.resize({
        snapToGrid: isGridEnabled ? snap : 1,
        resizeLimits: { width: Math.max(gw, 1), height: Math.max(gd, 1) },
        constraint: { minX: 0, minY: 0, maxX: stw, maxY: std },
    });
    rect.on('resizestart', hideInfoWindow);
    rect.on('resizedone', (e) => handleWallMouseUp(e, wall));
};

const disableWallDrag = (node) => {
    if (!node) return;
    const element = SVG.adopt(node);
    stopDrag(element);
};

const disableWallResize = (node) => {
    if (!node) return;
    const element = SVG.adopt(node);

    if(!element) {
        return;
    }

    const id = element.node.id;

    if(!document.getElementById(id)) {
        return;
    }

    unselectize(element);
    stopResize(element);
};

// Wall rect
const Wall = ({ wall }) => {
    const [wasSelected, setWasSelected] = useState(false);

    const sel = useSelector((state) => state.store.selectedStoreElement);
    const isDragEnabled = useSelector((state) => state.store.isDragEnabled);
    const isGridEnabled = useSelector((state) => state.store.isGridEnabled);
    const snapToRuler = useSelector((state) => state.store.snapToRuler)
    
    const { storeData } = getStoreState();
    const rulers = storeData?.rulers;

    const rectRef = useRef();

    const id = wall.generated_id || wall.id;
    const nodeId = `Wall_${id}`;

    const width = Number(wall.width) || 0;
    const depth = Number(wall.depth) || 0;

    const gw = storeData?.floor_plan.grid_width;
    const gd = storeData?.floor_plan.grid_depth;

    const isSelected = !!sel && compareElements(wall, sel);

    // on wall click
    const handleWallClick = (event) => {
        if (!isSelected) {
            selectStoreElement(wall, true);
        }
    };

    useEffect(() => {
        if (isSelected && isDragEnabled) {
            enableWallResize(rectRef.current, wall);
            setWasSelected(true);
        } else {
            if (wasSelected) {
                disableWallResize(rectRef.current);
                setWasSelected(false);
            }
        }
        const node = rectRef.current;
        return () => { if (wasSelected) disableWallResize(node); };
    }, [isSelected, isDragEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!rectRef.current) return;
        disableWallDrag(rectRef.current);
        if (isDragEnabled) {
            enableWallDrag(rectRef.current, wall);
        }
    }, [isDragEnabled, isGridEnabled, width, depth, rulers, snapToRuler, gw, gd]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <rect
            ref={rectRef}
            id={nodeId}
            x={wall.positionX}
            y={wall.positionY}
            width={wall.width}
            height={wall.depth}
            fill={wallFillColor}
            stroke={wallStrokeColor}
            strokeWidth={1}
            onClick={handleWallClick}
            style={{ cursor: isDragEnabled ? 'move' : 'pointer' }}
        />
    );
};

// on rotated element dragmove
const onElementDragMove = (event, element) => {
    const el = getStoreElement(element) || element;

    const isRotated = Number(el.angle) !== 0;
    if (!isRotated) return;

    const { target } = event;

    const pos = {
        x: Number(target.getAttribute('x')),
        y: Number(target.getAttribute('y'))
    };

    const node = document.getElementById(`Element_${el.generated_id || el.id}`);
    
    const { width: ww, depth: wd } = getRotatedElementWrapperSize(el);

    const dw = (ww - el.width) / 2;
    const dd = (wd - el.depth) / 2;

    const x = pos.x + dw;
    const y = pos.y + dd;

    const rotate = `rotate(${el.angle}, ${pos.x + ww/2}, ${pos.y + wd/2})`;
    
    SVG.adopt(node).move(x, y).attr({ transform: rotate });
};

// hides info window
const hideInfoWindow = () => {
    store.dispatch(setIsInfoWindowVisible(false));
};

// shows info window
const showInfoWindow = () => {
    store.dispatch(setIsInfoWindowVisible(true));
};

// save changed position on dragend
const onElementDragEnd = (event, element) => {
    const { target } = event;
    
    const pos = getRectData(target);

    if (element.positionX !== pos.x || element.positionY !== pos.y) {
        const el = getStoreElement(element) || element;
        const updatedElement = { ...el, positionX: pos.x, positionY: pos.y };

        selectStoreElement(updatedElement);

        store.dispatch(updateFloorPlanElement(updatedElement));
        store.dispatch(setUnsavedModifications(true));
        //store.dispatisRotatedch(setHistory(btoa(JSON.stringify(storeData.floor_plan_elements))));
        //store.dispatch(setHistoryStep(historyStep+1));
    }

    showInfoWindow();
};

const enableElementDrag = (node, element) => {
    const rect = SVG.adopt(node);
    rect.draggable(makeSnapToGrid(element));
    rect.on('beforedrag', hideInfoWindow);
    rect.on('dragmove.namespace', (e) => onElementDragMove(e, element));
    rect.on('dragend', (e) => onElementDragEnd(e, element));
};

const disableElementDrag = (node) => {
    if (!node) return;
    const element = SVG.adopt(node);
    stopDrag(element);
};

// Project rect
const Element = ({ scale, element, onContextMenu }) => {
    // const [wasSelected, setWasSelected] = useState(false);

    const sel = useSelector((state) => state.store.selectedStoreElement);
    const isDragEnabled = useSelector((state) => state.store.isDragEnabled);
    const isGridEnabled = useSelector((state) => state.store.isGridEnabled);
    const snapToRuler = useSelector((state) => state.store.snapToRuler)

    const { storeData } = getStoreState();
    const rulers = storeData?.rulers;
    
    const st = storeData?.floor_plan; // store
    const stw = st ? st.width : 100; // store width
    const std = st ? st.depth : 100; // store depth

    const id = element.generated_id || element.id;
    const nodeId = `Element_${id}`;
    const frameNodeId = `ElementFrame_${id}`;

    const rectRef = useRef();
    const frameRef = useRef();

    const px = Number(element.positionX);
    const py = Number(element.positionY);
    
    const gw = storeData?.floor_plan.grid_width;
    const gd = storeData?.floor_plan.grid_depth;

    const { width: ww, depth: wd } = getRotatedElementWrapperSize(element);

    const diffWidth = (ww - element.width) / 2;
    const diffDepth = (wd - element.depth) / 2;

    const angle = Number(element.angle) || 0;

    const isRotated = angle !== 0;

    const rotate = `rotate(${angle}, ${px + ww/2}, ${py + wd/2})`;

    const isSelected = !!sel && compareElements(element, sel);
    
    const selectCurrentElement = (event) => {
        if (!isSelected) {
            selectStoreElement(element, true);
        }
    };

    useEffect(() => {
        if (!rectRef.current && !frameRef.current) return;

        disableElementDrag(rectRef.current);
        disableElementDrag(frameRef.current);

        if (isDragEnabled) {
            enableElementDrag((isRotated?frameRef:rectRef).current, element);
        }
    }, [isDragEnabled, isGridEnabled, isRotated, angle, rulers, stw, std, snapToRuler, gw, gd]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <g key={id}>
            <rect
                ref={rectRef}

                id={nodeId}

                x={px + diffWidth}
                y={py + diffDepth}
                width={element.width}
                height={element.depth}

                //fill={element.hex || defaultFillColor}
                fill={isSelected ? '#90EE90' : element.hex || defaultFillColor}
                stroke={defaultStrokeColor}
                strokeWidth={1}
                
                transform={rotate}

                style={{ cursor: isDragEnabled ? 'move' : 'pointer' }}

                onClick={selectCurrentElement}
                onTouchStart={selectCurrentElement}
                onContextMenu={e => onContextMenu(e, element)}
            />
            <rect
                ref={frameRef}

                id={frameNodeId}

                x={px}
                y={py}
                width={ww}
                height={wd}

                fill={frameFillColor}
                fillOpacity={0.01}
                stroke={frameStrokeColor}
                strokeWidth={1/scale}
                strokeDasharray={2/scale}
                strokeOpacity={0.5}

                visibility={isRotated ? 'visible' : 'hidden'}

                style={{ cursor: isDragEnabled ? 'move' : 'pointer' }}

                onClick={selectCurrentElement}
                onTouchMove={selectCurrentElement}
                onContextMenu={e => onContextMenu(e, element)}
            />
        </g>
    );
};

const rulerTextSize = 10;
const rulerTextOffset = 20;

// Ruler line
const Ruler = ({ item, maxWidth, maxHeight, scale = 1 }) => {
    const id = item.generated_id || item.id;
    const nodeId = `Ruler_${id}`;

    const v = item.type === 'vertical';

    const x = Number(item.x);
    const y = Number(item.y);

    const [rulerTextValue, setRulerTextValue] = useState(v ? x : y);
    const [rulerTextPos, setRulerTextPos] = useState({ x, y });

    const offset = rulerWidth + rulerTextOffset;
    
    const storeData = useSelector((state) => state.store.storeData);

    const st = storeData?.floor_plan; // store
    const stw = st ? st.width : 100; // store width
    const std = st ? st.depth : 100; // store depth

    const handleRulerClick = (event) => {
        selectStoreElement(item, true);
    };

    useEffect(() => {
        const rulerNode = document.getElementById(nodeId);
        if (!rulerNode) return;

        const ruler = SVG.adopt(rulerNode);

        const { stw, std } = getStoreSizes();

        // ruler.draggable(draggableSnapToGrid);
        ruler.draggable({
            snapToGrid: 1,
            minX: item.type == 'vertical' ? 0 : -2147483647,
            minY: item.type == 'horizontal' ? 0 : -2147483647,
            maxX: stw,
            maxY: std
        });

        ruler.on('beforedrag', hideInfoWindow);

        ruler.on('dragmove.namespace', function(event) {
            const { target } = event;

            const pos = {
                x: Number(target.getAttribute('x1')),
                y: Number(target.getAttribute('y1'))
            };

            setRulerTextValue(v ? pos.x : pos.y);
            setRulerTextPos(pos);
        });

        ruler.on('dragend', event => {
            const { target } = event;

            const pos = { x: target.getAttribute('x1'), y: target.getAttribute('y1') };

            const { moveElementsWithRuler } = getStoreState();

            if (moveElementsWithRuler) {
                const { storeData } = getStoreState();
                const storeElements = storeData?.floor_plan_elements;

                const rulers = storeData?.rulers;
                const thisRuler = rulers.find(r => compareElements(item, r, 'ruler'));

                const { x, y } = thisRuler;

                const snapElements = storeElements.filter(el => {
                    const elementPos = Number(el[v ? 'positionX' : 'positionY']);
                    const rulerPos = v ? x : y;
                    return Math.abs(Number(rulerPos) - Number(elementPos)) < 1;
                });

                snapElements.forEach(el => {
                    store.dispatch(updateFloorPlanElement(
                        { ...el, [v ? 'positionX' : 'positionY']: v ? pos.x : pos.y }
                    ));
                })
            }
            
            store.dispatch(updateRuler({ ...item, ...pos }));

            showInfoWindow();
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        store.dispatch(setIsSceneUpdated(false));
    }, [x, y])

    const textOffsetX = (rulerTextPos.x > stw / 2 ? -offset : offset);
    const textOffsetY = (rulerTextPos.y > std / 2 ? -offset : offset + rulerTextSize/scale);

    const { isGridEnabled } = getStoreState();

    return (
        <g>
            <line
                id={nodeId}
                x1={v ? item.x : -2147483647}
                x2={v ? item.x : 2147483647}
                y1={v ? -2147483647 : item.y}
                y2={v ? 2147483647 : item.y}
                stroke={rulerColor}
                strokeOpacity={1}
                strokeWidth={rulerWidth / scale}
                style={{ cursor: v ? 'col-resize' : 'row-resize' }}
                onClick={handleRulerClick}
            />
        </g>
    );
};

export default ElementsLayer;
